import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Avatar,
  Grid,
  LinearProgress,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CreateIcon from '@mui/icons-material/Create';
import LinkIcon from '@mui/icons-material/Link';
import { generateResumeTemplate, uploadFile, getTemplates, fetchJobPosting } from '../../services/api';
import LoadingOverlay from '../Common/LoadingOverlay';
import TemplateSelector from './TemplateSelector';
import TemplatePickerButton from './TemplatePickerButton';
import PricingModal from '../Billing/PricingModal';
import {
  generateButtonSx,
  mobileStickyFooterSx,
  formTabsSx,
  fileDropZoneSx,
  fileDropZoneIconSx,
  uploadedFileCardSx,
} from '../Common/formStyles';

const MAX_AI_GENERATIONS = 5;
const FIELD_SIZE = 'small';

const TAB_PERSONAL = 0;
const TAB_BACKGROUND = 1;
const TAB_TARGET_JOB = 2;

const BG_TAB_WRITE = 0;
const BG_TAB_UPLOAD = 1;

const TabPanel = ({ children, value, index, sx }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={[{ pt: 2, pb: 0.5 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
  >
    {value === index && children}
  </Box>
);

const InputForm = ({ onResumeGenerated, initialData, subscriptionStatus, isSplitView = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactEditor = isSplitView && !isMobile;
  const fieldColSize = isCompactEditor ? 12 : 6;

  const [activeTab, setActiveTab] = useState(TAB_PERSONAL);
  const [backgroundTab, setBackgroundTab] = useState(BG_TAB_WRITE);
  const [formData, setFormData] = useState({
    resumeTitle: '',
    fullName: '',
    email: '',
    phone: '',
    location: '',
    title: '',
    prompt: '',
    jobDescription: '',
    pageCount: 1,
  });
  // eslint-disable-next-line no-unused-vars
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageKey, setProfileImageKey] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [existingResume, setExistingResume] = useState(null);
  const [existingResumeKey, setExistingResumeKey] = useState(null);
  const [resumeSourceTextKey, setResumeSourceTextKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('single-column-classic');
  const [templates, setTemplates] = useState([]);
  const [upgradePricingOpen, setUpgradePricingOpen] = useState(false);
  const [jobFetchLoading, setJobFetchLoading] = useState(false);
  const [jobFetchMeta, setJobFetchMeta] = useState(null);
  const [jobFetchError, setJobFetchError] = useState('');

  const jobUrlDetected = React.useMemo(() => {
    const trimmed = formData.jobDescription.trim();
    if (!trimmed) return null;
    const firstLine = trimmed.split('\n')[0].trim();
    if (/^https?:\/\/\S+$/i.test(firstLine)) {
      return firstLine.replace(/[.,;:!?)]+$/, '');
    }
    const match = trimmed.match(/https?:\/\/[^\s<>"']+/i);
    return match ? match[0].replace(/[.,;:!?)]+$/, '') : null;
  }, [formData.jobDescription]);

  React.useEffect(() => {
    getTemplates()
      .then(setTemplates)
      .catch((err) => console.error('Failed to fetch templates:', err));
  }, []);

  React.useEffect(() => {
    if (initialData) {
      const sourceData = initialData.formData || initialData;

      setFormData({
        resumeTitle: sourceData.resumeTitle || initialData.resumeTitle || '',
        fullName: sourceData.fullName || initialData.fullName || '',
        email: sourceData.email || initialData.email || '',
        phone: sourceData.phone || initialData.phone || '',
        location: sourceData.location || initialData.location || '',
        title: sourceData.title || sourceData.jobTitle || initialData.jobTitle || '',
        prompt: sourceData.prompt || initialData.prompt || '',
        jobDescription: sourceData.jobDescription || initialData.jobDescription || '',
        pageCount: sourceData.pageCount || initialData.pageCount || 1,
      });

      if (initialData.templateId) {
        setSelectedTemplateId(initialData.templateId);
      }

      if (initialData.profileImageKey) {
        setProfileImageKey(initialData.profileImageKey);
        setProfileImageUrl(initialData.profileImageUrl);
        setProfileImagePreview(initialData.profileImageUrl);
      } else {
        setProfileImageKey(null);
        setProfileImageUrl(null);
        setProfileImagePreview(null);
      }

      if (initialData.resumeFileKey) {
        setExistingResumeKey(initialData.resumeFileKey);
      } else {
        setExistingResumeKey(null);
      }

      if (initialData.resumeSourceTextKey) {
        setResumeSourceTextKey(initialData.resumeSourceTextKey);
      } else {
        setResumeSourceTextKey(null);
      }

      if (!initialData.resumeFileKey && !initialData.resumeSourceTextKey) {
        setExistingResume(null);
      } else if (initialData.resumeSourceTextKey && !initialData.resumeFileKey) {
        setExistingResume({ name: 'Previously uploaded resume' });
      }
    } else if (initialData === null) {
      setFormData({
        resumeTitle: '',
        fullName: '',
        email: '',
        phone: '',
        location: '',
        title: '',
        prompt: '',
        jobDescription: '',
        pageCount: 1,
      });
      setSelectedTemplateId('single-column-classic');
      setProfileImageKey(null);
      setProfileImageUrl(null);
      setProfileImagePreview(null);
      setExistingResume(null);
      setExistingResumeKey(null);
      setResumeSourceTextKey(null);
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'jobDescription') {
      setJobFetchMeta(null);
      setJobFetchError('');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFetchJobPosting = async () => {
    if (!jobUrlDetected) return;

    setJobFetchLoading(true);
    setJobFetchError('');
    setError('');

    try {
      const result = await fetchJobPosting(formData.jobDescription.trim());
      setFormData((prev) => ({ ...prev, jobDescription: result.text }));
      setJobFetchMeta({
        sourceUrl: result.sourceUrl,
        title: result.title,
        method: result.method,
      });
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load job posting';
      setJobFetchError(message);
      setJobFetchMeta(null);
    } finally {
      setJobFetchLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Profile image must be less than 2MB');
        return;
      }

      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedImageTypes.includes(file.type.toLowerCase())) {
        setError('Invalid file type. Allowed: JPG, PNG, GIF, WEBP');
        return;
      }

      setUploading(true);
      setError('');

      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        const result = await uploadFile(file, 'profile');
        setProfileImageKey(result.key);
        setProfileImageUrl(result.url);
        setProfileImage(file);
      } catch (err) {
        setError('Failed to upload profile image: ' + (err.response?.data?.message || err.message));
        setProfileImagePreview(null);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Resume PDF must be less than 2MB');
        return;
      }

      const isPdf =
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        setError('Only PDF files are accepted. Convert your Word document to PDF first.');
        return;
      }

      setUploading(true);
      setError('');

      try {
        const result = await uploadFile(file, 'resume');
        setExistingResumeKey(result.key);
        setExistingResume(file);
        setBackgroundTab(BG_TAB_UPLOAD);
        setActiveTab(TAB_BACKGROUND);
      } catch (err) {
        setError('Failed to upload resume: ' + (err.response?.data?.message || err.message));
      } finally {
        setUploading(false);
      }
    }
  };

  const hasWrittenBackground = formData.prompt.trim().length > 0;
  const hasUploadedResume = !!existingResumeKey || !!resumeSourceTextKey;

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
    setProfileImageKey(null);
    setProfileImageUrl(null);
  };

  const handleRemoveResume = () => {
    setExistingResume(null);
    setExistingResumeKey(null);
    setResumeSourceTextKey(null);
  };

  const handleGenerate = async () => {
    if (!formData.fullName.trim()) {
      setError('Please enter your full name');
      setActiveTab(TAB_PERSONAL);
      return;
    }

    if (!hasWrittenBackground && !hasUploadedResume) {
      setError('Describe your background or upload a PDF resume');
      setActiveTab(TAB_BACKGROUND);
      setBackgroundTab(hasUploadedResume ? BG_TAB_UPLOAD : BG_TAB_WRITE);
      return;
    }

    if (uploading) {
      setError('Please wait for file uploads to complete');
      return;
    }

    setLoading(true);
    setError('');
    setLoadingProgress(0);
    setLoadingMessage('Starting resume generation...');

    try {
      const resumeData = {
        ...formData,
        templateId: selectedTemplateId,
        profileImageKey,
        profileImageUrl,
        resumeFileKey: existingResumeKey,
        resumeSourceTextKey,
        hasExistingResume: hasUploadedResume,
      };

      const result = await generateResumeTemplate(resumeData, (progressData) => {
        setLoadingProgress(progressData.progress || 0);
        setLoadingMessage(progressData.message || 'Processing...');
      });

      onResumeGenerated(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to generate resume');
    } finally {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  const renderTemplatePicker = ({ fullWidth = true } = {}) => (
    <TemplatePickerButton
      templateId={selectedTemplateId}
      templates={templates}
      onClick={() => setTemplateSelectorOpen(true)}
      compact={!fullWidth}
      label="Resume template"
    />
  );

  const renderProfileUpload = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      {profileImagePreview ? (
        <>
          <Avatar src={profileImagePreview} sx={{ width: 64, height: 64 }} />
          <Box>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              Profile photo
            </Typography>
            <IconButton onClick={handleRemoveImage} color="error" size="small" sx={{ ml: -0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </>
      ) : (
        <Button
          component="label"
          variant="outlined"
          size={FIELD_SIZE}
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload Photo'}
          <input
            type="file"
            hidden
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageUpload}
            disabled={uploading}
          />
        </Button>
      )}
    </Box>
  );

  const renderResumeTitleField = () => (
    <TextField
      fullWidth
      size={FIELD_SIZE}
      label="Resume Title (Optional)"
      name="resumeTitle"
      value={formData.resumeTitle}
      onChange={handleInputChange}
      placeholder="e.g., Software Engineer — Google"
      helperText="Give this resume a unique name. We'll auto-generate one if left blank."
      sx={{ mb: 2 }}
    />
  );

  const renderPersonalFields = () => (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={fieldColSize}>
        <TextField
          fullWidth
          size={FIELD_SIZE}
          label="Full Name *"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} sm={fieldColSize}>
        <TextField
          fullWidth
          size={FIELD_SIZE}
          label="Professional Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} sm={fieldColSize}>
        <TextField
          fullWidth
          size={FIELD_SIZE}
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} sm={fieldColSize}>
        <TextField
          fullWidth
          size={FIELD_SIZE}
          label="Phone"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size={FIELD_SIZE}
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
        />
      </Grid>
    </Grid>
  );

  const renderPagesField = () => (
    <FormControl fullWidth size={FIELD_SIZE}>
      <InputLabel>Pages</InputLabel>
      <Select name="pageCount" value={formData.pageCount} onChange={handleInputChange} label="Pages">
        <MenuItem value={1}>1 Page</MenuItem>
        <MenuItem value={2}>2 Pages</MenuItem>
        <MenuItem value={3}>3 Pages</MenuItem>
      </Select>
    </FormControl>
  );

  const renderDocxToPdfGuide = () => (
    <Alert
      severity="info"
      icon={<InfoOutlinedIcon fontSize="small" />}
      sx={{
        height: '100%',
        py: 1.25,
        alignItems: 'flex-start',
        display: 'flex',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        Have a Word (.docx) file? Save it as PDF first:
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        • <strong>Microsoft Word:</strong> File → Save As → PDF
        <br />
        • <strong>Google Docs:</strong> File → Download → PDF Document
        <br />
        • <strong>Mac Pages:</strong> File → Export To → PDF
        <br />
        • <strong>LibreOffice:</strong> File → Export as PDF
      </Typography>
    </Alert>
  );

  const renderResumePdfUpload = () => (
    <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12} sm={isCompactEditor ? 12 : 5}>
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          {existingResume ? (
            <Box sx={[uploadedFileCardSx, { minHeight: 160 }]}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'background.paper'),
                  flexShrink: 0,
                }}
              >
                <PictureAsPdfIcon color="primary" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {existingResume.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ready for AI parsing
                </Typography>
              </Box>
              <IconButton onClick={handleRemoveResume} color="error" size="small" aria-label="Remove resume">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              component="label"
              sx={[
                fileDropZoneSx,
                { minHeight: 160, flex: 1 },
                uploading && { opacity: 0.7, pointerEvents: 'none', transform: 'none' },
              ]}
            >
              <Box sx={fileDropZoneIconSx}>
                <PictureAsPdfIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                {uploading ? 'Uploading…' : 'Drop your PDF here'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                or click to browse · max 2 MB
              </Typography>
              <Chip
                label="PDF only"
                size="small"
                sx={{
                  mt: 0.5,
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(129,140,248,0.2)' : 'rgba(99,102,241,0.12)',
                  color: 'primary.main',
                }}
              />
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={handleResumeUpload}
                disabled={uploading}
              />
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={isCompactEditor ? 12 : 7}>
        {renderDocxToPdfGuide()}
      </Grid>
    </Grid>
  );

  const renderWriteTab = () => (
    <TextField
      fullWidth
      size={FIELD_SIZE}
      multiline
      minRows={isMobile ? 8 : 10}
      maxRows={16}
      label="Skills, experience, and goals"
      name="prompt"
      value={formData.prompt}
      onChange={handleInputChange}
      placeholder="I'm a senior software engineer with 5 years of experience in React, Node.js…"
      inputProps={{ maxLength: 5000 }}
      helperText={`${formData.prompt.length}/5,000 characters`}
      error={formData.prompt.length > 5000}
    />
  );

  const renderBackgroundTab = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Provide your experience by <strong>writing a summary</strong> or <strong>uploading a PDF resume</strong> — at least one is required.
      </Typography>

      <Tabs
        value={backgroundTab}
        onChange={(_, value) => setBackgroundTab(value)}
        variant="fullWidth"
        sx={[
          formTabsSx,
          {
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 1, fontSize: '0.8125rem' },
          },
        ]}
      >
        <Tab
          icon={<CreateIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Write"
          sx={{ gap: 0.75 }}
        />
        <Tab
          icon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Upload PDF"
          sx={{ gap: 0.75 }}
        />
      </Tabs>

      {backgroundTab === BG_TAB_WRITE && renderWriteTab()}
      {backgroundTab === BG_TAB_UPLOAD && renderResumePdfUpload()}
    </>
  );

  const renderTargetJobTab = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Paste a <strong>job link</strong> (Lever, Greenhouse, company careers pages) or the full posting text.
        We&apos;ll fetch and extract the description from supported links automatically.
      </Typography>

      {jobUrlDetected && !jobFetchMeta && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={jobFetchLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
            onClick={handleFetchJobPosting}
            disabled={jobFetchLoading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {jobFetchLoading ? 'Loading posting…' : 'Load from link'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {jobUrlDetected}
          </Typography>
        </Box>
      )}

      {jobFetchMeta && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJobFetchMeta(null)}>
          Loaded job posting
          {jobFetchMeta.title ? ` — ${jobFetchMeta.title}` : ''}
          {jobFetchMeta.sourceUrl ? (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
              Source: {jobFetchMeta.sourceUrl}
            </Typography>
          ) : null}
        </Alert>
      )}

      {jobFetchError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setJobFetchError('')}>
          {jobFetchError}
        </Alert>
      )}

      <TextField
        fullWidth
        size={FIELD_SIZE}
        multiline
        minRows={isMobile ? 10 : 12}
        maxRows={18}
        label="Job link or description"
        name="jobDescription"
        value={formData.jobDescription}
        onChange={handleInputChange}
        placeholder="https://jobs.lever.co/company/role-id — or paste the full job description…"
        inputProps={{ maxLength: 10000, style: { whiteSpace: 'pre-wrap', lineHeight: 1.6 } }}
        helperText={`${formData.jobDescription.length}/10,000 characters`}
        error={formData.jobDescription.length > 10000}
      />
    </>
  );

  const renderPersonalTab = () => (
    <>
      {renderProfileUpload()}
      {renderResumeTitleField()}
      {renderPersonalFields()}
      <Box sx={{ mt: 2, maxWidth: isCompactEditor ? '100%' : 280 }}>{renderPagesField()}</Box>
    </>
  );

  const renderGenerateButton = () => {
    const aiGenCount = initialData?.id ? initialData?.aiGenerationCount || 0 : 0;
    const hasSubscription = subscriptionStatus?.isActive;
    const limitReached = !hasSubscription && initialData?.id && aiGenCount >= MAX_AI_GENERATIONS;
    const remaining = MAX_AI_GENERATIONS - aiGenCount;

    if (limitReached) {
      return (
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 2,
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(237,108,2,0.08)' : 'rgba(255,152,0,0.06)'),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LockIcon color="warning" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700} color="warning.main">
              AI Generation Limit Reached (5/5)
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            You have used all {MAX_AI_GENERATIONS} AI generations for this resume. Subscribe to Pro for unlimited
            generations on all resumes.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            startIcon={<StarIcon />}
            onClick={() => setUpgradePricingOpen(true)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Upgrade to Pro — €30/month
          </Button>
        </Box>
      );
    }

    return (
      <>
        {!hasSubscription && initialData?.id && aiGenCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                AI generations used
              </Typography>
              <Typography
                variant="caption"
                color={remaining <= 1 ? 'warning.main' : 'text.secondary'}
                fontWeight={remaining <= 1 ? 700 : 400}
              >
                {aiGenCount} / {MAX_AI_GENERATIONS}
                {remaining <= 1 && ` · ${remaining} remaining`}
              </Typography>
            </Box>
            <Tooltip title={`${remaining} AI generation${remaining === 1 ? '' : 's'} remaining for this resume`}>
              <LinearProgress
                variant="determinate"
                value={(aiGenCount / MAX_AI_GENERATIONS) * 100}
                color={remaining <= 1 ? 'warning' : 'primary'}
                sx={{ borderRadius: 1, height: 4 }}
              />
            </Tooltip>
          </Box>
        )}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleGenerate}
          disabled={loading || uploading}
          sx={generateButtonSx}
        >
          {uploading ? 'Uploading files…' : 'Generate Resume with AI'}
        </Button>
      </>
    );
  };

  const renderFormHeader = () => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant={isMobile || isCompactEditor ? 'h6' : 'h5'}
        fontWeight={800}
        color="text.primary"
        letterSpacing="-0.02em"
        sx={{ mb: 1.5 }}
      >
        Resume details
      </Typography>
      {renderTemplatePicker()}
    </Box>
  );

  const renderFormTabs = () => (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant={isMobile || isCompactEditor ? 'scrollable' : 'fullWidth'}
        scrollButtons={isMobile || isCompactEditor ? 'auto' : false}
        allowScrollButtonsMobile
        sx={[
          formTabsSx,
          isCompactEditor && {
            '& .MuiTab-root': {
              minHeight: 44,
              py: 1,
              px: 1.5,
              fontSize: '0.8125rem',
              minWidth: 'auto',
            },
          },
        ]}
      >
        <Tab
          icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Personal"
          sx={{ gap: 0.75 }}
        />
        <Tab
          icon={<EditNoteIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Background"
          sx={{ gap: 0.75 }}
        />
        <Tab
          icon={<WorkOutlineIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Target Job"
          sx={{ gap: 0.75 }}
        />
      </Tabs>

      <TabPanel value={activeTab} index={TAB_PERSONAL}>
        {renderPersonalTab()}
      </TabPanel>
      <TabPanel value={activeTab} index={TAB_BACKGROUND}>
        {renderBackgroundTab()}
      </TabPanel>
      <TabPanel value={activeTab} index={TAB_TARGET_JOB}>
        {renderTargetJobTab()}
      </TabPanel>
    </Box>
  );

  const formContent = (
    <>
      {renderFormHeader()}
      {error && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{ mb: 2, bgcolor: 'background.paper', borderColor: 'error.main' }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      {renderFormTabs()}
    </>
  );

  return (
    <>
      {loading && <LoadingOverlay message={loadingMessage} progress={loadingProgress} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0 }}>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            p: isMobile || isCompactEditor ? 0 : 3,
          }}
        >
          {isMobile ? (
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>{formContent}</Box>
          ) : isCompactEditor ? (
            <Box sx={{ px: 2, pt: 2, pb: 1, width: '100%' }}>{formContent}</Box>
          ) : (
            <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{formContent}</Box>
            </Paper>
          )}
        </Box>

        <Box
          sx={[
            mobileStickyFooterSx,
            isCompactEditor && { px: 2 },
            !isMobile && !isCompactEditor && { px: 3 },
          ]}
        >
          {renderGenerateButton()}
        </Box>
      </Box>

      <TemplateSelector
        open={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        onSelectTemplate={(templateId) => {
          setSelectedTemplateId(templateId);
          setTemplateSelectorOpen(false);
        }}
        selectedTemplateId={selectedTemplateId}
      />

      <PricingModal
        open={upgradePricingOpen}
        onClose={() => setUpgradePricingOpen(false)}
        resumeId={initialData?.id}
        reason="generation_limit"
      />
    </>
  );
};

export default InputForm;
